import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { situacaoEhTerminal } from "../../enums/enums";
import { descricaoDaSituacao, useSituacoes } from "../../services/situacoes";
import { useEditorArquivos } from "../EditorArquivos";
import "./style.css";

import { MdAttachFile, MdSend } from "react-icons/md";
import Swal from "sweetalert2";

// Caixa de mensagem do chat do chamado. Regra CHA-039 do Raptor (03/11/2026): texto e arquivos numa ação só,
// com o print onde o cursor estava. A caixa é um editor (contenteditable): Ctrl+V de print, clipe ou arrastar
// põem o arquivo no ponto do cursor, e Ctrl+A + Del apaga tudo, arquivos inclusive. No envio o texto vai puro,
// com `[[arquivo:i]]` na posição de cada arquivo (o servidor grava `[[anexo:id]]`); só vai o arquivo que ainda
// está na caixa. `onSend(texto, arquivos)` devolve true quando a ação foi gravada, e só então a caixa é limpa.
// A tela chama `adicionarArquivos(files, range)` (ref) para o arquivo arrastado para o chat. O editor em si é o
// de `components/EditorArquivos` (o mesmo da Nova Solicitação).

const MAX_ARQUIVOS = 10;
const LIMITE_BYTES = 40 * 1024 * 1024;

export const MessageChat = forwardRef(function MessageChat({ onSend, chamado, enviando = false }, ref) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const situacoes = useSituacoes();
  const editor = useEditorArquivos({
    editorRef,
    bloqueado: enviando,
    maxArquivos: MAX_ARQUIVOS,
    limiteBytes: LIMITE_BYTES,
    podeAdicionar: () => !chamadoTravado(),
    porOnde: "por mensagem",
  });
  const { temConteudo, atualizarConteudo, adicionarArquivos } = editor;

  // Chamado em situação terminal (Encerrado/Cancelado, e o Baixado legado do Lizard) não aceita ação nova —
  // a lista de ids está em enums.js, o rótulo vem da tabela chamado_situacao.
  function chamadoTravado() {
    if (!situacaoEhTerminal(chamado?.situacao)) return false;
    Swal.fire({
      title: "Alerta!",
      text: `Este Chamado se encontra na situação ${descricaoDaSituacao(
        situacoes,
        chamado?.situacao
      )},
      e portanto você não pode mais criar ação neste Chamado.`,
      icon: "warning",
    });
    return true;
  }

  useImperativeHandle(ref, () => ({ adicionarArquivos }));

  function handleSandAnexo() {
    if (chamadoTravado()) return;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  }

  async function handleSandMsg() {
    const ed = editorRef.current;
    if (!onSend || enviando || !ed) return;
    if (chamadoTravado()) return;
    const { texto, arquivos } = editor.coletar();
    if (!texto && !arquivos.length) return;
    const enviada = await onSend(texto, arquivos);
    if (enviada) editor.limpar();
  }

  return (
    <div className="message_chat">
      <div className="right_footer">
        <span>
          <MdAttachFile title="Enviar Arquivo" size={24} onClick={() => handleSandAnexo()} />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => adicionarArquivos(e.target.files)}
            multiple // Permitir a seleção de vários arquivos
          />
        </span>
        <div
          id="acaoInput"
          ref={editorRef}
          className="caixa_mensagem"
          contentEditable={!enviando}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Mensagem"
          data-placeholder="Mensagem"
          onPaste={editor.aoColar}
          onInput={atualizarConteudo}
        />
        <span className={enviando || !temConteudo ? "enviando" : undefined}>
          <MdSend title="Enviar Mensagem" size={24} onClick={() => handleSandMsg()} />
        </span>
      </div>
    </div>
  );
});

export default MessageChat;
